<template>
  <div class="page-container">
    <PageHeader title="全量日志">
      <template #actions>
        <el-button type="danger" size="small" @click="handleCopyLog">复制迁移日志</el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-select v-model="query.logPlatform" clearable placeholder="平台" size="small" style="width: 100px" @change="handleSearch">
        <el-option label="PC" :value="1"></el-option>
        <el-option label="微信" :value="2"></el-option>
      </el-select>
      <el-input v-model.trim="query.logUserName" placeholder="操作人" size="small" clearable></el-input>
      <el-input v-model.trim="query.logUserId" placeholder="用户ID" size="small" clearable @keyup.enter.native="handleSearch"></el-input>
      <el-input v-model.trim="query.logApi" placeholder="接口名" size="small" clearable></el-input>
      <el-select v-model="query.logType" clearable placeholder="操作类型" size="small" @change="handleSearch">
        <el-option v-for="item in logTypeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[20, 50, 80, 100, 300, 500, 1000]"
        :total="total"
        row-id-key="logId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
      >
        <template #cell-logPlatform="{ row }">
          {{ row.logPlatform === 2 ? '微信' : 'PC' }}
        </template>
        <template #cell-logType="{ row }">
          <el-tag size="mini" :type="getLogTypeTag(row.logType)">{{ getLogTypeLabel(row.logType) }}</el-tag>
        </template>
      </aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无日志" icon="el-icon-document-copy"></EmptyState>
    </TablePanel>
  </div>
</template>

<script>
import { getSysLogCopyList, updateSysLogCopyDo } from '@/api/api'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

// 操作类型选项
const LOG_TYPE_OPTIONS = [
  { label: '其他', value: 0, tag: 'info' },
  { label: '查询', value: 1, tag: '' },
  { label: '新增', value: 2, tag: 'success' },
  { label: '编辑', value: 3, tag: 'warning' },
  { label: '删除', value: 4, tag: 'danger' }
]

// 表格列
const FULL_LOG_COLUMNS = [
  { type: 'index', label: '序号', width: 60 },
  { prop: 'logPlatform', label: '平台', width: 70 },
  { prop: 'logType', label: '类型', width: 80 },
  { prop: 'logApi', label: '接口名', minWidth: 160 },
  { prop: 'logPath', label: '接口地址', minWidth: 180 },
  { prop: 'description', label: '接口具体类型', minWidth: 140 },
  { prop: 'logIp', label: 'IP地址', width: 120 },
  { prop: 'logParams', label: '携带参数', minWidth: 200 },
  { prop: 'logUserId', label: '用户ID', width: 80 },
  { prop: 'logUserName', label: '操作人员', width: 100 },
  { prop: 'logTime', label: '操作时间', width: 150 }
]

export default {
  name: 'FullLogPage',
  components: { AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      total: 0,
      list: [],
      columns: FULL_LOG_COLUMNS,
      logTypeOptions: LOG_TYPE_OPTIONS,
      query: { logPlatform: '', logType: '', logUserId: '', logUserName: '', logApi: '', currentPage: 1, pageSize: 20 }
    }
  },
  created () { this.fetchList() },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const params = {
          logPlatform: this.query.logPlatform || undefined,
          logType: this.query.logType !== '' && this.query.logType !== null ? this.query.logType : undefined,
          logUserId: this.query.logUserId || undefined,
          logUserName: this.query.logUserName || undefined,
          logApi: this.query.logApi || undefined,
          currentPage: this.query.currentPage,
          pageSize: this.query.pageSize
        }
        const res = await getSysLogCopyList(params)
        this.list = Array.isArray(res.data) ? res.data : []
        this.total = Number(res.total || 0)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    // 复制迁移日志
    async handleCopyLog () {
      try {
        const { value } = await this.$prompt('请输入复制日志条数(万)?', '确定要复制迁移吗', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPattern: /^\d+$/,
          inputErrorMessage: '请输入数字',
          type: 'warning'
        })
        const loading = this.$loading({ lock: true, text: '迁移中...', spinner: 'el-icon-loading', background: 'rgba(0,0,0,0.7)' })
        try {
          const res = await updateSysLogCopyDo({ nums: value })
          loading.close()
          if (res.data) {
            this.$message.success('迁移成功!')
            this.fetchList()
          }
        } catch (e) {
          loading.close()
        }
      } catch (e) { /* 用户取消 */ }
    },
    getLogTypeLabel (value) {
      const target = this.logTypeOptions.find(item => item.value === value)
      return target ? target.label : '其他'
    },
    getLogTypeTag (value) {
      const target = this.logTypeOptions.find(item => item.value === value)
      return target ? target.tag : 'info'
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { logPlatform: '', logType: '', logUserId: '', logUserName: '', logApi: '', currentPage: 1, pageSize: 20 }
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
</style>
